// ============================================================================
// types_signaling.ts
// Refs et callbacks pour la signalisation WS côté régie (et mode esclave).
// ============================================================================
import React from 'react';
import { RemoteDeviceInfo }    from './types_webrtc';
import { WhipNegotiationInfo } from './types_webrtc';
import { SdpLogger }           from './types_callbacks';
import { SlaveState }          from './types_webrtc';
import { DeviceGeoReport }     from './types_media';
// ─── Régie ────────────────────────────────────────────────────────────────────
export interface SignalingRefs {
  wsRef:                    React.MutableRefObject<WebSocket | null>;
  wsReconnectTimeoutRef:    React.MutableRefObject<ReturnType<typeof setTimeout>  | null>;
  wsHbIntervalRef:          React.MutableRefObject<ReturnType<typeof setInterval> | null>;
  wsPongTimeoutRef:         React.MutableRefObject<ReturnType<typeof setTimeout>  | null>;
  peerConnectionRef:        React.MutableRefObject<RTCPeerConnection | null>;
  iceCandidateBufferRef:    React.MutableRefObject<Map<string, RTCIceCandidateInit[]>>;
  remoteDescSetRef:         React.MutableRefObject<Map<string, boolean>>;
  remoteConnectionsRef:     React.MutableRefObject<Map<string, any>>;
  remoteDeviceInfoRef:      React.MutableRefObject<Map<string, RemoteDeviceInfo>>;
  deviceSupportedCodecsRef: React.MutableRefObject<Map<string, string[]>>;
  requiredCodecRef:         React.MutableRefObject<{ codec: string; profile: string } | null>;
  whipOfferSdpRef:          React.MutableRefObject<string | undefined>;
  whipAnswerSdpRef:         React.MutableRefObject<string | undefined>;
  whipStatsIntervalRef:     React.MutableRefObject<ReturnType<typeof setInterval> | null>;
  currentStreamRef:         React.MutableRefObject<MediaStream | null>;
  canvasStreamRef:          React.MutableRefObject<MediaStream | null>;
  audioMixerDestRef:        React.MutableRefObject<MediaStreamAudioDestinationNode | null>;
}
export interface SignalingCallbacks {
  addSdpLog:              SdpLogger;
  setWhipInfo:            (updater: (prev: WhipNegotiationInfo) => WhipNegotiationInfo) => void;
  setIsStreaming:         (v: boolean) => void;
  setRemoteConnections:   (updater: (prev: Map<string, any>) => Map<string, any>) => void;
  setNegotiatedWhipCodec: (codec: any) => void;
  setCodecProbeState:     (state: 'idle' | 'probing' | 'done' | 'error') => void;
  loadDevices:            () => void;
  startWebRTCStreaming:   () => Promise<void>;
  connectToDevice:        (device_id: string) => Promise<void>;
  injectStream?:          (sourceId: string, stream: MediaStream) => void;
  /**
   * Réarme le rôle Réalisateur d'un device qui vient de se connecter, s'il le
   * porte sur sa fiche. Le rôle est une propriété du device, pas de la session :
   * sans ce rappel, un smartphone marqué Réalisateur dans « Gestion des Sources
   * Distantes » repartirait en mode normal à chaque reconnexion.
   */
  applyDirectorRoleOnConnect?: (device_id: string) => Promise<void>;
  /** Réaffirme la prise de mesures à un appareil qui vient de se connecter. */
  applyMetricsLogOnConnect?: (device_id: string) => void;
  /** Journalise la connexion d'un smartphone dans l'onglet Logs du créateur (device_connected/device_reconnected). */
  /** `ip` : adresse du téléphone vue par le microservice (champ optionnel de device_connected). */
  /** `userAgent` : User-Agent du WebSocket du téléphone (champ optionnel de device_connected). */
  logDeviceConnected?: (device_id: string, ip?: string, userAgent?: string) => void;
  /** Symétrique de logDeviceConnected — le log ne garde que le nom de la caméra, pas le motif. */
  logDeviceDisconnected?: (device_id: string) => void;
  /**
   * L'appareil n'a plus de flux, mais on l'attend : sa place de deck est tenue et
   * sa vignette passe en « EN ATTENTE » au lieu de disparaître.
   *
   * Appelé sur deux chemins bien distincts — la déconnexion annoncée par le
   * serveur, et l'échec WHEP transitoire, qui lui ne passe par aucun
   * `device_disconnected` et vidait donc l'emplacement en silence.
   */
  markDeviceAwaiting?:  (device_id: string) => void;
  /** Le flux est revenu : la source reprend sa place, avec son image. */
  clearDeviceAwaiting?: (device_id: string) => void;
  /**
   * Qualité du lien montant d'un device, telle qu'il la mesure lui-même.
   * `level` vaut 1 à 4 ; `sample` porte le détail chiffré.
   */
  onDeviceNetworkQuality?: (device_id: string, level: number, sample: unknown) => void;
  /**
   * Position et direction de visée d'un device, rapportées par lui-même
   * (message `device_geo`). Alimente la carte de la régie.
   */
  onDeviceGeo?:           (device_id: string, report: DeviceGeoReport) => void;
  /**
   * État du contrôle de la régie. Plusieurs régies peuvent être connectées à la
   * même chaîne — une seule pilote. Sans ce retour, une régie évincée continuait
   * d'envoyer des offres que le serveur ignore, en croyant piloter.
   */
  setControlState?:       (state: RegieControlState) => void;
  /**
   * Le contrôle vient d'être RETIRÉ à cette régie, au profit de `takenBy`.
   *
   * Notification à part, et surtout PAS un champ de `RegieControlState` : le
   * serveur envoie `control_revoked` puis, dans la foulée, un `control_state` à
   * tout le monde. Les deux gestionnaires étant asynchrones, leurs mises à jour
   * peuvent être regroupées dans un même rendu — un drapeau porté par l'état
   * serait alors écrasé avant d'avoir pu déclencher quoi que ce soit, et
   * l'opérateur ne verrait rien. Un appel direct ne peut pas se perdre ainsi.
   */
  /**
   * `reason` dit COMMENT la main est partie : 'accepted' (nous venons de
   * l'accorder — rien à signaler, l'opérateur vient de cliquer), 'forced'
   * (notre régie n'a pas répondu à la demande), 'requested' (place reprise
   * sans demande — microservice antérieur au 23/08/2026).
   */
  onControlRevoked?:      (takenBy: string, reason: string, takenByAddr: string | null) => void;
  /**
   * Cette régie vient d'OBTENIR le contrôle. `reason` dit pourquoi :
   *
   *   'first'       — elle est arrivée sur une place libre (cas courant) ;
   *   'requested'   — l'opérateur vient de cliquer sur « Prendre le contrôle » ;
   *   'holder_left' — la régie qui pilotait s'est fermée, la main lui revient.
   *
   * Seul le dernier cas mérite d'être signalé : les deux autres résultent d'un
   * geste ou d'une situation que l'opérateur connaît déjà. Alerter à chaque
   * ouverture ferait vite passer le message pour du bruit.
   */
  onControlGranted?:      (reason: string) => void;
  /**
   * Une autre régie DEMANDE la main. Ce message n'arrive qu'au porteur, et il ne
   * s'accompagne d'aucune bascule : rien ne bouge tant qu'il n'a pas répondu.
   */
  onControlRequested?:    (request: RegieControlRequest) => void;
  /** Notre demande est posée, en attente de la réponse du porteur. */
  onControlRequestPending?: (
    holderLabel: string | null,
    graceS: number,
    holderAddr: string | null,
  ) => void;
  /**
   * Notre demande n'aboutira pas. `reason` dit pourquoi :
   *
   *   'refused'          — le porteur a dit non. C'est une RÉPONSE : le forçage
   *                        n'est pas proposé, la demande est close ;
   *   'busy'             — une autre régie attend déjà une réponse ;
   *   'obsolete'         — le contrôle a changé de mains entre-temps ;
   *   'too_soon'         — forçage tenté avant la fin du délai de grâce ;
   *   'nothing_to_force' — plus aucune demande en attente à forcer.
   */
  onControlRequestDenied?: (reason: string, by: string | null, byAddr: string | null) => void;
  /** La régie qui demandait la main a renoncé (ou s'est fermée). */
  onControlRequestCancelled?: (label: string, addr: string | null) => void;
  /**
   * La liste des sources externes de la chaîne (HLS, Xtream, caméras RTMP) a
   * changé. Adressé à TOUTES les régies, spectatrices comprises : sans cela,
   * une régie ouverte avant l'ajout d'une caméra ne la voit jamais, et se met à
   * piloter sur une liste périmée à la seconde où la main lui revient.
   *
   * Le message ne porte que le motif : la liste elle-même reste servie par HTTP,
   * et c'est au front d'aller la relire.
   */
  onSourcesChanged?: (reason: string) => void;
  /**
   * Quota de durée cumulée de diffusion OU d'enregistrement du créateur (cf.
   * duration_guard.py côté microservice). `kind` vaut 'warning' (10 min
   * restantes) ou 'limit_reached' (arrêt automatique en cours). `scope` dit
   * LEQUEL des deux quotas indépendants est concerné — un `limit_reached` sur
   * 'recording' arrête l'enregistrement côté admin-api SANS toucher à la
   * diffusion : le front doit resynchroniser isRecording en conséquence, pas
   * se contenter d'afficher le message (cf. recordingLimitReachedRef /
   * handleStopRecording('server_limit') dans LiveStreamManager.tsx — sans
   * quoi le compteur régie continue de tourner alors que plus rien n'est
   * écrit sur le disque).
   */
  onDurationNotice?: (kind: 'warning' | 'limit_reached', message: string, scope: 'broadcasting' | 'recording') => void;
  /**
   * Un plafond de quota (durationMaxBroadcasting/durationMaxRecording/
   * quotaDisk) vient de changer côté admin-api pendant que cette régie est
   * ouverte — poussé par AdminQuotaBroadcastService dès qu'un admin modifie
   * son propre compte, au lieu d'attendre le prochain refreshAdmin() (achat,
   * bascule de chaîne, login). `patch` ne porte que les champs effectivement
   * modifiés.
   */
  onQuotaUpdated?: (patch: { durationMaxBroadcasting?: number; durationMaxRecording?: number; quotaDisk?: number }) => void;
  /**
   * Le serveur dit quels angles de téléphones ont leur jumeau « audio du programme » PRÊT (publié, avec
   * vidéo et audio). La régie ne déclare un jumeau aux spectateurs qu'à cette condition. Cf. angleAudio.ts.
   */
  onAngleAudioState?: (ready: string[]) => void;
}

/** Une régie spectatrice qui demande la main au porteur. */
export interface RegieControlRequest {
  /** Session de la demandeuse, attribuée par le serveur. */
  sessionId: string;
  /** Comment elle se présente à l'écran du porteur. */
  label:     string;
  /**
   * Adresse depuis laquelle elle est connectée. Les libellés se ressemblent
   * tous (« régie ») : c'est l'adresse qui dit au porteur QUI lui demande la
   * main, et donc s'il doit la céder. Vide si le microservice est antérieur.
   */
  addr:      string;
  /**
   * Silence au bout duquel la demandeuse pourra forcer le passage. Le porteur
   * doit le savoir : ne pas répondre n'est pas refuser.
   */
  graceS:    number;
}

/**
 * Comment on désigne une régie à l'écran : son libellé, puis son adresse.
 *
 * Les libellés ne distinguent rien — toutes les régies s'annoncent « régie ».
 * L'adresse est le seul élément qui permette à l'opérateur de savoir de quel
 * poste on lui parle. Elle manque face à un microservice antérieur au
 * 24/08/2026 : on retombe alors sur le libellé seul, sans parenthèses vides.
 */
export const decrireRegie = (label: string | null, addr: string | null): string => {
  const nom = label?.trim() || 'une autre régie';
  return addr?.trim() ? `${nom} (${addr.trim()})` : nom;
};

/** Une régie connectée à la chaîne, telle que le serveur la décrit. */
export interface RegieControlPeer {
  sessionId: string;
  label:     string;
  /** Adresse de connexion. Vide si le microservice est antérieur au 24/08/2026. */
  addr:      string;
  isHolder:  boolean;
}

/** Ce que le microservice dit à CHAQUE régie de l'état du contrôle. */
export interface RegieControlState {
  /** Cette régie-ci pilote-t-elle ? */
  isHolder:     boolean;
  /** Libellé de celle qui pilote, si ce n'est pas nous. */
  holderLabel:  string | null;
  /** Adresse de celle qui pilote, si ce n'est pas nous. */
  holderAddr:   string | null;
  /** Nombre de régies connectées à cette chaîne, celle-ci comprise. */
  regieCount:   number;
  /** Identité attribuée par le serveur à cette connexion. */
  sessionId:    string | null;
  /**
   * Toutes les régies connectées, la nôtre comprise, la plus ancienne d'abord.
   * C'est ce qui permet de NOMMER les spectatrices au porteur au lieu de les
   * compter. Vide face à un microservice antérieur : les bandeaux retombent
   * alors sur le décompte.
   */
  peers:        RegieControlPeer[];
}
export interface SignalingConfig {
  serverUrl:    string;
  streamPath:   string;
  signalingUrl: string;
}
// ─── Mode esclave ─────────────────────────────────────────────────────────────
export interface SlaveRefs {
  slaveWsRef:               React.MutableRefObject<WebSocket | null>;
  slavePcRef:               React.MutableRefObject<RTCPeerConnection | null>;
  slaveIceBufRef:           React.MutableRefObject<RTCIceCandidateInit[]>;
  slaveRemoteDescSetRef:    React.MutableRefObject<boolean>;
  slaveShouldReconnectRef:  React.MutableRefObject<boolean>;
  slaveReconnectTimeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
  slaveSettingsRef:         React.MutableRefObject<{ masterWsUrl: string; deviceId: string; password: string; label: string }>;
  canvasStreamRef:          React.MutableRefObject<MediaStream | null>;
  currentStreamRef:         React.MutableRefObject<MediaStream | null>;
  audioMixerDestRef:        React.MutableRefObject<MediaStreamAudioDestinationNode | null>;
}
export interface SlaveCallbacks {
  addSdpLog:          SdpLogger;
  setIsSlave:         (v: boolean) => void;
  setSlaveState:      (state: SlaveState) => void;
  initCanvasStream:   (videoEl: HTMLVideoElement, fps?: number) => MediaStream;
  getSourceVideoEl:   () => HTMLVideoElement | null;
  getCanvasPreviewEl: () => HTMLVideoElement | null;
}